import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { faArrowDownLeftAndArrowUpRightToCenter } from "@fortawesome/pro-solid-svg-icons/faArrowDownLeftAndArrowUpRightToCenter";
import { faArrowUpRightAndArrowDownLeftFromCenter } from "@fortawesome/pro-solid-svg-icons/faArrowUpRightAndArrowDownLeftFromCenter";
import { faCheckCircle } from "@fortawesome/pro-solid-svg-icons/faCheckCircle";
import { faClose } from "@fortawesome/pro-solid-svg-icons/faClose";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons/faQuestionCircle";
import { faWindowMinimize } from "@fortawesome/pro-solid-svg-icons/faWindowMinimize";
import { faXmarkCircle } from "@fortawesome/pro-solid-svg-icons/faXmarkCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
// eslint-disable-next-line no-restricted-imports
import AntdModal, { ModalProps as AntdModalProps } from "antd/es/modal";
// eslint-disable-next-line no-restricted-imports
import { ModalFunc as AntdModalFunc } from "antd/es/modal/confirm";
// eslint-disable-next-line no-restricted-imports
import { ModalFuncProps as AntdModalFuncProps } from "antd/es/modal/Modal";
// eslint-disable-next-line no-restricted-imports
import useModalAntd from "antd/es/modal/useModal";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";

import { useNonNullableContext } from "@mapmycustomers/shared/util/react";

import Button, { ButtonProps, ButtonType } from "../Button";
import { useConfigProvider } from "../ConfigProvider";
import IConfigProviderContext from "../ConfigProvider/types/IConfigProviderContext";

import ModalContext, { ModalContextValue } from "./ModalContext";
import ModalsContext from "./ModalsContext";
import ModalState from "./ModalState";
import ModalStatus from "./ModalStatus";

const wrapBlock = bem("mmc-modal-wrapper");
const block = bem("mmc-modal");

export type ModalFuncProps = Omit<
  AntdModalFuncProps,
  "cancelButtonProps" | "okButtonProps" | "type"
> & {
  cancelButtonProps?: ButtonProps;
  okButtonProps?: ButtonProps;
  type: ModalType;
};

// we can support changing size if needed in future by changing className
export type ModalConfigUpdateModalProps = Omit<ModalFuncProps, "icon" | "size" | "type">;

export type ModalConfigUpdate =
  | ((prevConfig: AntdModalFuncProps) => AntdModalFuncProps)
  | AntdModalFuncProps;

export type ModalType = "confirm" | "error" | "info" | "success" | "warning";

export type ModalSize = "normal" | "small";

export type ModalFunc = (args: ModalFuncProps) => {
  destroy: () => void;
  update: (configUpdate: ModalConfigUpdate) => void;
};

export type ModalFunctions = { show: ModalFunc };

const getBemModifiers = ({
  icon,
  scrollable,
  size,
  type,
  ...args
}: {
  closable?: boolean;
  collapsed?: boolean;
  dynamic?: boolean;
  icon?: ReactNode;
  minimized?: boolean;
  scrollable?: boolean;
  size?: ModalSize;
  type?: ModalType;
}): Record<string, boolean | undefined> => {
  return {
    ...{
      normal: size === "normal",
      scrollable,
      small: size === "small",
      ["with-icon"]: !!icon,
    },
    ...(type ? { [type]: true } : {}),
    ...args,
  };
};

const getIcon = (type?: ModalType, icon?: ReactNode) => {
  const getFaIcon = (type: ModalType) => {
    switch (type) {
      case "confirm":
        return faQuestionCircle;
      case "error":
        return faXmarkCircle;
      case "info":
        return faInfoCircle;
      case "success":
        return faCheckCircle;
      case "warning":
        return faExclamationCircle;
    }
  };

  const wrapIcon = (icon: ReactNode) => {
    return <span className={block("icon")}>{icon}</span>;
  };

  if (icon) {
    return wrapIcon(icon);
  }

  if (icon === undefined && type) {
    return wrapIcon(<FontAwesomeIcon icon={getFaIcon(type)} />);
  }

  return null;
};

const getAntModalFuncProps = (
  {
    cancelButtonProps,
    cancelText,
    className,
    closable = false,
    closeIcon = <FontAwesomeIcon icon={faClose} />,
    content,
    icon: iconProp,
    okButtonProps,
    okText,
    onCancel,
    onOk,
    type,
    ...props
  }: ModalFuncProps,
  configProvider?: IConfigProviderContext
): AntdModalFuncProps => {
  const icon = getIcon(type, iconProp);
  return {
    ...props,
    className: cn(block(getBemModifiers({ closable, icon, size: "small", type })), className),
    closable,
    closeIcon,
    content: (
      <div className={block("body")}>
        <div className={block("content")}>{content}</div>
        <div className={block("footer")}>
          {type === "confirm" && (
            <Button onClick={onCancel} {...cancelButtonProps}>
              {cancelText ??
                configProvider?.formatMessage("ui.modal.footer.cancelText") ??
                "Cancel"}
            </Button>
          )}
          <Button danger={type === "error"} onClick={onOk} type="primary" {...okButtonProps}>
            {okText ?? configProvider?.formatMessage("ui.modal.footer.okText") ?? "Okay"}
          </Button>
        </div>
      </div>
    ),
    icon,
  };
};

const createModal = ({
  configProvider,
  createModalFactory,
  modalProps,
}: {
  configProvider?: IConfigProviderContext;
  createModalFactory: AntdModalFunc;
  modalProps: ModalFuncProps;
}) => {
  const { onCancel, onOk, ...restProps } = modalProps;

  const modal = createModalFactory(
    getAntModalFuncProps(
      {
        ...restProps,
        onCancel: () => {
          onCancel?.();
          modal.destroy();
        },
        onOk: () => {
          onOk?.();
          modal.destroy();
        },
      },
      configProvider
    )
  );

  return modal;
};

export const useModal = (): [ModalFunctions, ReactElement] => {
  const configProvider = useConfigProvider();
  // The hook runs in the calling component, so it sees the Modal that component renders inside.
  const parentModalContext = useContext(ModalContext);
  const [modalAntd, modalContext] = useModalAntd();

  const open = useCallback(
    (props: ModalFuncProps) => {
      return createModal({
        configProvider,
        createModalFactory: modalAntd[props.type],
        // The caller's own zIndex wins. The opener's level only fills in when it gives none.
        modalProps: { ...props, zIndex: props.zIndex ?? parentModalContext?.zIndex },
      });
    },
    [configProvider, modalAntd, parentModalContext?.zIndex]
  );

  const modal = useMemo(
    () => ({
      show: (args: ModalFuncProps) => open(args),
    }),
    [open]
  );

  return [modal, modalContext];
};

export const showModal = (props: ModalFuncProps): ReturnType<ModalFunc> => {
  return createModal({ createModalFactory: AntdModal[props.type], modalProps: props });
};

export type ModalProps = Omit<AntdModalProps, "cancelButtonProps" | "okButtonProps"> & {
  actions?: ReactNode;
  cancelButtonProps?: AntdModalProps["cancelButtonProps"] & { type?: ButtonType };
  collapsible?: boolean;
  dynamic?: boolean;
  footerExtra?: ReactNode;
  icon?: ReactNode;
  okButtonProps?: AntdModalProps["okButtonProps"] & { type?: ButtonType };
  onIdChange?: (id?: string) => void;
  onStatusChange?: (status: ModalStatus) => void;
  scrollable?: boolean;
  size?: ModalSize;
  subtitle?: ReactNode;
  titleExtra?: ReactNode;
  type?: ModalType;
};

const Modal = ({
  actions,
  cancelButtonProps,
  cancelText,
  children,
  className,
  closable: closableProp = true,
  closeIcon,
  dynamic = false,
  footer,
  footerExtra,
  icon: iconProp,
  okButtonProps,
  okText,
  onCancel,
  onIdChange,
  onOk,
  onStatusChange,
  open,
  scrollable,
  size = "normal",
  subtitle,
  title,
  titleExtra,
  type,
  width: widthProp,
  zIndex: zIndexProp,
  ...props
}: ModalProps) => {
  const configProvider = useConfigProvider();

  // A Modal that opens from inside another one has to paint above it. Left to antd it would use
  // the default of 1000 and hide behind any opener that raised its own level.
  const parentModalContext = useContext(ModalContext);
  const zIndex = zIndexProp ?? parentModalContext?.zIndex;
  const modalContext = useMemo<ModalContextValue>(() => ({ zIndex }), [zIndex]);

  const [id, setId] = useState<string>("");
  const { collapse, destroy, initialize, minimize, modals, restore } =
    useNonNullableContext(ModalsContext);

  useEffect(() => {
    if (open && dynamic) {
      const id = initialize();
      setId(id);
      return () => {
        destroy(id);
        setId("");
      };
    }
  }, [initialize, dynamic, destroy, open]);

  const changeStatusDisabled = useMemo(() => {
    return dynamic && modals.some((modal) => modal.id !== id && modal.status !== "normal");
  }, [id, dynamic, modals]);

  const { bottom, height, right, status, width }: ModalState = useMemo(() => {
    return id ? modals.find((modal) => modal.id === id)! : { id: "", status: "normal" };
  }, [id, modals]);

  const icon = useMemo(() => getIcon(type, iconProp), [iconProp, type]);

  const normal = status === "normal";
  const minimized = status === "minimized";
  const collapsed = status === "collapsed";

  const closable = !minimized && closableProp;

  useEffect(() => {
    onIdChange?.(id);
  }, [id, onIdChange]);

  useEffect(() => {
    onStatusChange?.(status);
  }, [status, onStatusChange]);

  const handleCollapse = useCallback(() => {
    collapse(id);
  }, [id, collapse]);

  const handleMinimize = useCallback(() => {
    minimize(id);
  }, [id, minimize]);

  const handleRestore = useCallback(() => {
    restore(id);
  }, [id, restore]);

  const renderTitle = () => {
    if (!title) {
      return null;
    }

    return (
      <div className={block("header")} onClick={handleRestore}>
        <div className={block("header-title-actions")}>
          {icon}
          <div className={block("header-title")}>{title}</div>
          {!minimized && actions && <div className={block("header-actions")}>{actions}</div>}
        </div>
        {!minimized && subtitle && <div className={block("header-subtitle")}>{subtitle}</div>}
        {!minimized && titleExtra && <div className={block("header-extra")}>{titleExtra}</div>}
      </div>
    );
  };

  const renderDynamicActions = () => {
    if (!dynamic || minimized) {
      return null;
    }

    return (
      <Tooltip
        title={
          changeStatusDisabled
            ? configProvider.formatMessage("ui.modal.minimize.disabledTooltip")
            : undefined
        }
      >
        <div className={block("dynamic-actions")}>
          <Tooltip
            title={
              changeStatusDisabled
                ? undefined
                : configProvider.formatMessage("ui.modal.minimize.tooltip")
            }
          >
            <Button
              disabled={changeStatusDisabled}
              icon={<FontAwesomeIcon icon={faWindowMinimize} />}
              iconOnly
              onClick={handleMinimize}
              size="small"
              type="control"
            />
          </Tooltip>
          <Tooltip
            title={
              changeStatusDisabled
                ? undefined
                : configProvider.formatMessage(
                    normal ? "ui.modal.collapse.tooltip" : "ui.modal.expand.tooltip"
                  )
            }
          >
            <Button
              disabled={changeStatusDisabled}
              icon={
                <FontAwesomeIcon
                  icon={
                    normal
                      ? faArrowDownLeftAndArrowUpRightToCenter
                      : faArrowUpRightAndArrowDownLeftFromCenter
                  }
                />
              }
              iconOnly
              onClick={normal ? handleCollapse : handleRestore}
              size="small"
              type="control"
            />
          </Tooltip>
        </div>
      </Tooltip>
    );
  };

  const renderFooter = () => {
    if (minimized) {
      return null;
    }

    if (footer !== undefined) {
      return footer;
    }

    return (
      <div className={block("footer")}>
        {footerExtra && <div className={block("footer-extra")}>{footerExtra}</div>}
        <div className={block("footer-actions")}>
          <Button onClick={onCancel} {...cancelButtonProps}>
            {cancelText ?? configProvider.formatMessage("ui.modal.footer.cancelText")}
          </Button>
          <Button danger={type === "error"} onClick={onOk} type="primary" {...okButtonProps}>
            {okText ?? configProvider.formatMessage("ui.modal.footer.okText")}
          </Button>
        </div>
      </div>
    );
  };

  // Wraps the whole dialog, not only its children, so a Modal opened from the title, the actions
  // or the footer reads this level too.
  return (
    <ModalContext.Provider value={modalContext}>
      <AntdModal
        centered={normal}
        className={cn(
          block(
            getBemModifiers({
              closable,
              collapsed,
              dynamic,
              icon,
              minimized,
              scrollable,
              size,
              type,
            })
          ),
          className
        )}
        closable={closable}
        closeIcon={
          <Tooltip title={configProvider.formatMessage("ui.modal.close.tooltip")}>
            {closeIcon ?? <FontAwesomeIcon icon={faClose} />}
          </Tooltip>
        }
        footer={renderFooter()}
        mask={normal}
        maskClosable={normal}
        onCancel={onCancel}
        open={open}
        style={!normal ? { bottom, height, right, width } : undefined}
        title={
          !!title || dynamic ? (
            <>
              {renderTitle()}
              {renderDynamicActions()}
            </>
          ) : null
        }
        width={width ?? widthProp}
        wrapClassName={cn(wrapBlock({ collapsed, minimized }))}
        zIndex={zIndex}
        {...props}
      >
        {!minimized && children}
      </AntdModal>
    </ModalContext.Provider>
  );
};

export default Modal;
